import React, { PropTypes, Component } from 'react';
import { connect } from 'react-redux';
import { Scatter, Chart } from 'react-chartjs-2';
import { localize } from 'linksoft';
import actions from './actions';
import ChartBase from '../chartsBase/chart-base'
import FullscreenWrapper from '../chart-fullscreen-wrapper/chart-fullscreen-wrapper';

const Resx = localize('App');

function mapStateToProps(state) {
    return {
        coordinates: state.axisCharts.coordinates,
        processing: state.axisCharts.processing,
    };
}

const mapDispatchToProps = {
    loadData: actions.loadData
};

@connect(mapStateToProps, mapDispatchToProps)
class AxisCharts extends ChartBase {
    static propTypes = {
        axisId: PropTypes.number.isRequired,
        coordinates: PropTypes.arrayOf(PropTypes.object),
        processing: PropTypes.bool,
        options: PropTypes.object.isRequired
    };

    componentDidMount() {
        this.props.loadData(this.props.axisId);
    }

    render() {
        const { processing, options } = this.props;

        if (options.tooltips.customText) {
            options.tooltips.callbacks = {
                label: function (tooltipItem, data) {
                    return data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
                        .metadata.tooltipText;
                },
            };
        }
        return (
            <div>
                {processing
                    ? this.progressLoader()
                    :
                    <div>
                        <label className="chartTitle">{Resx.App.ChartPlanimetry}</label>
                        <FullscreenWrapper>
                            <Scatter data={this.getDataForXYChart()} options={this.xyOptions()} />
                        </FullscreenWrapper>

                        <label className="chartTitle">{Resx.App.ChartVerticalAlignment}</label>
                        <FullscreenWrapper>
                            <Scatter data={this.getDataForChainageChart()} options={this.zProfileOptions()} />
                        </FullscreenWrapper>
                    </div>
                }
            </div>
        );
    }

    xyOptions() {
        var points = (this.props.coordinates || []);

        var offset = 1;

        var xAxisMax = Math.max.apply(null, points.map(item => (item.x))) + offset;
        var yAxisMax = Math.max.apply(null, points.map(item => (item.y))) + offset;
        var xAxisMin = Math.min.apply(null, points.map(item => (item.x))) - offset;
        var yAxisMin = Math.min.apply(null, points.map(item => (item.y))) - offset;

        return {
            legend: false,
            tooltips: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem, data) => {
                        return (
                            data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
                                .metadata.tooltipText || Resx.App.Profile
                        ); //show measurement tooltip, or text 'Profile' form profile points
                    },
                },
            },
            scales: {
                xAxes: [{
                    display: true,
                    labelString: Resx.App.CoordinateX,
                    type: 'linear',
                    position: 'bottom',
                    gridLines: {
                        color: 'rgba(137, 139, 186, .3)',
                        drawOnChartArea: true
                    },
                    ticks: {
                        min: this.infitineToValue(xAxisMin),
                        max: this.infitineToValue(xAxisMax)
                    }
                }],
                yAxes: [{
                    display: true,
                    labelString: Resx.App.CoordinateY,
                    type: 'linear',
                    position: 'bottom',
                    gridLines: {
                        color: 'rgba(137, 139, 186, .3)',
                        drawOnChartArea: true
                    },
                    ticks: {
                        min: this.infitineToValue(yAxisMin),
                        max: this.infitineToValue(yAxisMax)
                    }
                }]
            }
        }
    }

    zProfileOptions() {
        var points = (this.props.coordinates || []);

        var offset = 1;

        var chainageAxisMax = Math.max.apply(null, points.map(item => (item.chainage))) + offset;
        var zAxisMax = Math.max.apply(null, points.map(item => (item.z))) + offset;
        var chainageAxisMin = Math.min.apply(null, points.map(item => (item.chainage))) - offset;
        var zAxisMin = Math.min.apply(null, points.map(item => (item.z))) - offset;

        return {
            legend: false,
            tooltips: {
                enabled: true,
                callbacks: {
                    label: (tooltipItem, data) => {
                        return (
                            data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
                                .metadata.tooltipText || Resx.App.Profile
                        ); //show measurement tooltip, or text 'Profile' form profile points
                    },
                },
            },
            scales: {
                xAxes: [{
                    display: true,
                    labelString: Resx.App.Chainage,
                    type: 'linear',
                    position: 'bottom',
                    gridLines: {
                        color: 'rgba(137, 139, 186, .3)',
                        drawOnChartArea: true
                    },
                    ticks: {
                        min: this.infitineToValue(chainageAxisMin),
                        max: this.infitineToValue(chainageAxisMax)
                    }
                }],
                yAxes: [{
                    display: true,
                    labelString: Resx.App.CoordinateZ,
                    type: 'linear',
                    position: 'bottom',
                    gridLines: {
                        color: 'rgba(137, 139, 186, .3)',
                        drawOnChartArea: true
                    },
                    ticks: {
                        min: this.infitineToValue(zAxisMin),
                        max: this.infitineToValue(zAxisMax)
                    }
                }]
            }
        }
    }

    getOrderedPoints() {
        var orderedPoints = (this.props.coordinates || []).slice();
        orderedPoints.sort(this.compareChainage);
        return orderedPoints;
    }

    getDataForXYChart() {
        var points = this.getOrderedPoints().map(item => ({ x: item.x, y: item.y }));

        return {
            datasets: [{
                label: 'XY Dataset',
                data: points,
                borderColor: 'rgba(81, 86, 209, 1)',
                pointBackgroundColor: 'rgba(81, 86, 209, .7)',
                pointHoverBackgroundColor: 'rgba(81, 86, 209, 1)',
                pointBorderColor: 'rgba(81, 86, 209, .7)',
                pointHoverBorderColor: 'rgba(81, 86, 209, 1)',
                borderWidth: 3,
                pointRadius: 7,
                pointHoverRadius: 10,
                pointBorderWidth: 2,
                fill: false,
                tension: 0,
                showLine: true,
                offset: true,
                padding: 20
            }],
            ticks: {
                min: 0
            }
        };
    }

    getDataForChainageChart() {
        var points = this.getOrderedPoints().map(item => ({ x: item.chainage, y: item.z }));

        return {
            datasets: [{
                label: 'Z Dataset',
                data: points,
                borderWidth: 3,
                borderColor: 'rgba(242, 96, 96, 1)',
                pointBackgroundColor: 'rgba(242, 96, 96, .7)',
                pointHoverBackgroundColor: 'rgba(242, 96, 96, 1)',
                pointBorderColor: 'rgba(242, 96, 96, .7)',
                pointRadius: 7,
                pointHoverRadius: 10,
                pointBorderWidth: 2,
                backgroundColor: 'rgba(232, 69, 69, .15)',
                fill: true,
                tension: 0,
                showLine: true
            }]
        };
    }

    compareChainage(a, b) {
        if (a.chainage < b.chainage) {
            return -1;
        }
        if (a.chainage > b.chainage) {
            return 1;
        }
        // a must be equal to b
        return 0;
    }
}

export default AxisCharts;


// WEBPACK FOOTER //
// ./App/components/modules/charts/axis-charts.js